/**
 * Travel Mode Chips Component
 * Displays clickable travel mode chips/tabs
 */

import React, { useEffect, useRef } from 'react';
import {
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  brandColors,
  strokeColors,
} from '@/design-system/utils';

export interface TravelMode {
  id: string;
  icon: string;
  label: string;
}

export interface TravelModeChipsProps {
  modes: TravelMode[];
  activeMode: string;
  onModeChange: (modeId: string) => void;
  getIcon: (iconName: string) => React.ReactNode;
}

export const TravelModeChips: React.FC<TravelModeChipsProps> = ({
  modes,
  activeMode,
  onModeChange,
  getIcon,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault(); // 🔥 prevents vertical page scroll
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (!modes || modes.length === 0) return null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => {
        document.body.style.overflowY = 'hidden'; // lock vertical scroll
      }}
      onMouseLeave={() => {
        document.body.style.overflowY = ''; // restore
      }}
      style={{
        display: 'flex',
        padding: `${spacing[1]} ${spacing[2.5]}`,
        alignItems: 'center',
        gap: spacing[2.5],
        borderRadius: borders.radius.full,
        backgroundColor: backgroundColors.primary,
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        boxSizing: 'border-box',
      }}
      className='[&::-webkit-scrollbar]:hidden'
    >
      {modes.map(mode => {
        const isActive = activeMode === mode.id;
        const formattedLabel = mode.label.startsWith('By ')
          ? mode.label
          : `By ${mode.label}`;

        const iconElement = mode.icon ? getIcon(mode.icon) : null;
        const hasIcon = iconElement !== null && iconElement !== undefined;

        return (
          <button
            key={mode.id}
            onClick={() => onModeChange(mode.id)}
            style={{
              display: 'flex',
              gap: hasIcon ? spacing[1.5] : 0,
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: spacing[4],
              paddingRight: spacing[4],
              paddingTop: spacing[2],
              paddingBottom: spacing[2],
              borderRadius: borders.radius.full,
              backgroundColor: isActive ? '#fffae2' : backgroundColors.primary,
              border: `1px solid ${
                isActive ? brandColors.primary : strokeColors.lightest
              }`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {hasIcon && (
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#4c4c4c',
                }}
              >
                {iconElement}
              </div>
            )}
            <span
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.sm,
                fontWeight: isActive
                  ? typography.weight.semibold
                  : typography.weight.medium,
                color: '#4c4c4c',
                textAlign: 'center',
              }}
            >
              {formattedLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
};
